import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import { isValidPrice } from '@domain-group/javascript-utils'

import api from '@functions/api'

import type { CampaignQuoteTemplateItemModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { updateCampaignQuoteTemplateTotal } from '@redux/modules/campaignQuoteTemplate'

import { CreateMultipleCampaignQuoteTemplateItemsPayload } from '@models/types'
import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/campaignQuoteTemplateItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/campaignQuoteTemplateItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/campaignQuoteTemplateItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/campaignQuoteTemplateItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/campaignQuoteTemplateItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/campaignQuoteTemplateItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/campaignQuoteTemplateItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/campaignQuoteTemplateItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/campaignQuoteTemplateItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/campaignQuoteTemplateItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/campaignQuoteTemplateItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/campaignQuoteTemplateItem/DELETE_FAILURE'

export type CampaignQuoteTemplateItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: CampaignQuoteTemplateItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadCampaignQuoteTemplateItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().campaignQuoteTemplateItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/campaign_quote_template_items.json', options)
      .then(({ data }: { data: CampaignQuoteTemplateItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadCampaignQuoteTemplateItem(campaignQuoteTemplateItemId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.campaignQuoteTemplateItems.loadedForKeys]
    const loadedIds = [...state.campaignQuoteTemplateItems.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (campaignQuoteTemplateItemId && !loadedIds.includes(campaignQuoteTemplateItemId)){
      loadedIds.push(campaignQuoteTemplateItemId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/campaign_quote_template_items/${campaignQuoteTemplateItemId}.json`, options)
      .then(({ data }: { data: CampaignQuoteTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteTemplateItem(
  campaignQuoteTemplateItem: Partial<CampaignQuoteTemplateItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_template_item: campaignQuoteTemplateItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_template_items.json', options, config)
      .then(({ data }: { data: CampaignQuoteTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        // Update Quote Template Data
        if (isValidPrice(data.quote_total)){
          // @ts-expect-error TS2345
          dispatch(updateCampaignQuoteTemplateTotal(data.campaign_quote_template_id, data.quote_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createCampaignQuoteTemplateItems(
  orderType: 'bundle' | 'publication',
  itemPayload: CreateMultipleCampaignQuoteTemplateItemsPayload,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      campaign_quote_template_item: itemPayload,
      order_type: orderType,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/campaign_quote_template_items/create_multiple.json', options, config)
      .then(({ data }: { data: CampaignQuoteTemplateItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        const firstItem = data?.[0]

        // Update Quote Template Data using first item in array
        if (firstItem?.campaign_quote_template_id && firstItem?.quote_total){
          dispatch(updateCampaignQuoteTemplateTotal(firstItem.campaign_quote_template_id, firstItem.quote_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteTemplateItem(
  campaignQuoteTemplateItem: UpdateParams<CampaignQuoteTemplateItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_template_item: campaignQuoteTemplateItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/campaign_quote_template_items/${campaignQuoteTemplateItem.id}.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        // Update Quote Template Data
        if (isValidPrice(data.quote_total)){
          // @ts-expect-error TS2345
          dispatch(updateCampaignQuoteTemplateTotal(data.campaign_quote_template_id, data.quote_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateCampaignQuoteTemplateItemQtyOptions(
  campaignQuoteTemplateItemId: number,
  itemPayload: CampaignQuoteTemplateItemModel,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      campaign_quote_template_item: itemPayload,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(
      `/campaign_quote_template_items/${campaignQuoteTemplateItemId}/update_options.json`,
      options,
      config,
    )
      .then(({ data }: { data: CampaignQuoteTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        // Update Quote Template Data
        if (isValidPrice(data.quote_total)){
          // @ts-expect-error TS2345
          dispatch(updateCampaignQuoteTemplateTotal(data.campaign_quote_template_id, data.quote_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateCampaignQuoteTemplateItem(
  campaignQuoteTemplateItem: UpdateParams<CampaignQuoteTemplateItemModel>,
  options: EntityOptions = {},
) {
  const { id } = campaignQuoteTemplateItem

  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/campaign_quote_template_items/${id}/duplicate.json`, options, config)
      .then(({ data }: { data: CampaignQuoteTemplateItemModel }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        // Update Quote Template Data
        if (isValidPrice(data.quote_total)){
          // @ts-expect-error TS2345
          dispatch(updateCampaignQuoteTemplateTotal(data.campaign_quote_template_id, data.quote_total))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadDynamicCampaignQuoteTemplateItems(options: EntityOptions = {}) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(
      '/campaign_quote_template_items/dynamic_items.json',
      options,
    )
      .then(({ data }: { data: CampaignQuoteTemplateItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteCampaignQuoteTemplateItem(
  campaignQuoteTemplateItem: DeleteParams<CampaignQuoteTemplateItemModel>,
) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/campaign_quote_template_items/${campaignQuoteTemplateItem.id}.json`, {}, config)
      .then(({ data }: { data: {
        campaign_quote_template_id: number,
        id: number,
        quote_total: number,
      }, }) => {
        const normalizedJson = normalize(data, Schemas.CAMPAIGN_QUOTE_TEMPLATE_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        // Update Quote Template Data
        if (isValidPrice(data.quote_total)){
          dispatch(updateCampaignQuoteTemplateTotal(data.campaign_quote_template_id, data.quote_total))
        }

        if (data.campaign_quote_template_id){
          dispatch(loadDynamicCampaignQuoteTemplateItems({
            campaign_quote_template_id: data.campaign_quote_template_id,
          }))
        }

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: CampaignQuoteTemplateItemModuleState = initialState,
  action: Action = { type: '' },
): CampaignQuoteTemplateItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
